import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardSignature, Download, ExternalLink, Search, Mail, Ticket, FileText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SortableTh, makeCycleSort } from "@/components/SortableTh";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

// Sortable-column config. Accessors mirror what each column visually
// renders so what you see is what gets sorted.
const SIGNUP_SORT_COLUMNS = {
  signed_at: { accessor: (r) => new Date(r.signed_at || r.created_at || 0).getTime() },
  customer: { accessor: (r) => (r.business_name || r.contact_name || "").toLowerCase() },
  plan: { accessor: (r) => (r.plan_name || "").toLowerCase() },
  syncro: {
    accessor: (r) => {
      const n = Number(r.syncro_ticket_number);
      return Number.isFinite(n) ? n : -1;
    },
  },
  delivery: { accessor: (r) => (r.email_sent_at ? 2 : r.pdf_generated_at ? 1 : 0) },
};

export default function AdminInternetSignups() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  // Default: newest signup first, most useful when reviewing today's work.
  const [sort, setSort] = useState({ key: "signed_at", dir: "desc" });
  const cycleSort = makeCycleSort(setSort, "signed_at", "desc");

  async function reload() {
    try {
      const { data } = await api.get("/commissions/internet-signups?limit=200");
      setRows(data.signups || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load signups");
      setRows([]);
    }
  }
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const term = q.trim().toLowerCase();
    let out = rows;
    if (term) {
      out = out.filter((r) => [
        r.business_name, r.contact_name, r.email, r.phone,
        r.plan_name, r.partner_code, String(r.syncro_ticket_number || ""),
      ].filter(Boolean).some((v) => String(v).toLowerCase().includes(term)));
    }
    const col = SIGNUP_SORT_COLUMNS[sort.key];
    if (col) {
      const dir = sort.dir === "asc" ? 1 : -1;
      out = [...out].sort((a, b) => {
        const av = col.accessor(a);
        const bv = col.accessor(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return  1 * dir;
        return 0;
      });
    }
    return out;
  }, [rows, q, sort]);

  return (
    <div className="space-y-6" data-testid="admin-signups-page">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Signed signups</h1>
        <p className="text-sm text-slate-400 mt-1">
          Every internet signup completed on the public wizard, with the signed T&amp;C PDF, Syncro ticket link and delivery status. PDFs are also archived against the Syncro customer.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="signups-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search business, email, ticket #…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-800 bg-slate-900 text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm"
          />
        </div>
        <div className="text-xs text-slate-500 font-mono">
          {filtered ? `${filtered.length} shown` : "loading…"}
        </div>
      </div>

      {filtered === null ? (
        <div className="text-slate-500 text-sm py-12 text-center">Loading signed signups…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-12 text-center">
          <ClipboardSignature className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <div className="text-slate-300 font-medium">No signups yet</div>
          <div className="text-xs text-slate-500 mt-1">Signed orders from the public wizard will appear here with a downloadable PDF.</div>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <SortableTh sortKey="signed_at" sort={sort} onSort={cycleSort} label="Signed" testidPrefix="signups-sort" />
                <SortableTh sortKey="customer"  sort={sort} onSort={cycleSort} label="Customer" testidPrefix="signups-sort" />
                <SortableTh sortKey="plan"      sort={sort} onSort={cycleSort} label="Plan" testidPrefix="signups-sort" />
                <SortableTh sortKey="syncro"    sort={sort} onSort={cycleSort} label="Syncro" testidPrefix="signups-sort" />
                <SortableTh sortKey="delivery"  sort={sort} onSort={cycleSort} label="Delivery" testidPrefix="signups-sort" />
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-slate-950/50" data-testid={`signup-row-${s.id}`}>
                  <Td>
                    <div className="text-slate-100 font-medium">{fmtDate(s.signed_at || s.created_at)}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">{s.id.slice(0, 8)}</div>
                  </Td>
                  <Td>
                    <div className="text-slate-100 font-medium">{s.business_name || s.contact_name || "—"}</div>
                    <div className="text-xs text-slate-400">{s.contact_name || "—"}</div>
                    <div className="text-xs text-slate-500">{s.email}</div>
                    {s.partner_code && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-cyan-400 font-semibold">
                        via {s.partner_code}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <div className="text-slate-100">{s.plan_name || "—"}</div>
                    <div className="text-xs text-slate-500">${(s.plan_price ?? 0).toFixed?.(2) || s.plan_price || "—"}/mo</div>
                  </Td>
                  <Td>
                    {s.syncro_ticket_number ? (
                      <div className="text-slate-300 font-mono text-xs inline-flex items-center gap-1">
                        <Ticket className="w-3 h-3 text-cyan-400" /> #{s.syncro_ticket_number}
                      </div>
                    ) : (
                      <div className="text-xs text-amber-400">not pushed</div>
                    )}
                    {s.syncro_customer_id && (
                      <div className="text-[10px] text-slate-600 font-mono mt-0.5">cust {s.syncro_customer_id}</div>
                    )}
                  </Td>
                  <Td>
                    <div className="space-y-0.5">
                      <StatusPill ok={!!s.pdf_generated_at} label="PDF" icon={FileText} />
                      <StatusPill ok={!!s.email_sent_at} label="Email" icon={Mail} />
                    </div>
                  </Td>
                  <Td className="text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        data-testid={`signup-view-${s.id}`}
                        onClick={() => setDetail(s)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-800 hover:bg-slate-950 text-xs text-slate-300"
                      >
                        View
                      </button>
                      {s.pdf_url && (
                        <a
                          data-testid={`signup-pdf-${s.id}`}
                          href={s.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-cyan-500/10 border border-cyan-500/40 hover:bg-cyan-500/20 text-xs text-cyan-300 font-medium"
                        >
                          <Download className="w-3 h-3" /> PDF
                        </a>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && <DetailDrawer signup={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-4 py-3 font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function StatusPill({ ok, label, icon: Icon }) {
  return (
    <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold ${ok ? "text-emerald-400" : "text-slate-600"}`}>
      <Icon className="w-3 h-3" /> {label} {ok ? "sent" : "pending"}
    </div>
  );
}

function DetailDrawer({ signup, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-lg h-full bg-slate-900 border-l border-slate-800 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="signup-detail-drawer"
      >
        <div className="p-6 border-b border-slate-800 flex items-start justify-between gap-3 sticky top-0 bg-slate-900">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">Signed signup</div>
            <h2 className="font-display text-xl tracking-tight mt-1">{signup.business_name || signup.contact_name}</h2>
            <div className="text-xs text-slate-500 font-mono mt-1">{signup.id}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100 text-sm">Close</button>
        </div>
        <div className="p-6 space-y-6">
          <Section title="Customer">
            <Row label="Contact">{signup.contact_name}</Row>
            <Row label="Email">{signup.email}</Row>
            <Row label="Phone">{signup.phone}</Row>
            <Row label="Service address">{signup.service_address}</Row>
          </Section>
          <Section title="Plan">
            <Row label="Plan">{signup.plan_name}</Row>
            <Row label="Monthly">${signup.plan_price?.toFixed?.(2) ?? signup.plan_price ?? "—"}</Row>
            {signup.partner_code && <Row label="Partner">{signup.partner_code}</Row>}
          </Section>
          <Section title="Signature">
            <Row label="Typed name">{signup.typed_name}</Row>
            <Row label="Signed at">{fmtDate(signup.signed_at || signup.created_at)}</Row>
            <Row label="IP">{signup.signed_ip}</Row>
          </Section>
          <Section title="Syncro">
            <Row label="Ticket">{signup.syncro_ticket_number ? `#${signup.syncro_ticket_number}` : "—"}</Row>
            <Row label="Customer ID">{signup.syncro_customer_id || "—"}</Row>
          </Section>
          <Section title="Delivery">
            <Row label="PDF generated">{fmtDate(signup.pdf_generated_at)}</Row>
            <Row label="Email sent">{fmtDate(signup.email_sent_at)}</Row>
          </Section>
          {signup.pdf_url && (
            <a
              data-testid="signup-detail-pdf"
              href={signup.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-3 rounded-lg text-sm font-semibold"
            >
              <Download className="w-4 h-4" /> Download signed PDF <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{title}</div>
      <div className="rounded-lg border border-slate-800 bg-slate-950 divide-y divide-slate-800">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="flex items-start gap-4 px-3 py-2 text-sm">
      <div className="w-28 shrink-0 text-xs text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
      <div className="text-slate-200 font-medium flex-1 break-words">{children || "—"}</div>
    </div>
  );
}
